import React, { HTMLAttributes, ReactNode, useId } from 'react';
import { ExitIcon } from '../Icons';
import { modalHeaderVariant, modalVariants } from './modalVariants';
import { VariantProps } from 'class-variance-authority';
import cn from '@/utils/cn';

const CloseButtonClass = cn([
  'bg-white',
  'bg-opacity-70',
  'hover:bg-gray-200',
  'hover:text-gray-900',
  'rounded-full',
  'text-sm',
  'w-fit',
  'h-fit',
  'ms-auto',
  'inline-flex',
  'justify-center',
  'items-center',
  'text-emerge-navy'
]);

export interface ModalProps
  extends HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof modalVariants>,
    VariantProps<typeof modalHeaderVariant> {
  isOpen: boolean;
  onClose?: () => void;
  headerText?: string | ReactNode;
}

const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  headerText,
  children,
  variant,
  headerVariant,
  className
}) => {
  // Only used to associate the dialog with its own visible title (aria-labelledby) - stable for
  // this component instance, and namespaced by useId so multiple modals on the page (even if
  // only one is ever open at a time in practice) can't collide on the same id.
  const headerId = useId();

  const handleClose = () => {
    if (onClose) {
      onClose();
    }
  };

  return (
    <>
      {isOpen ? (
        <div
          id="static-modal"
          data-modal-backdrop="static"
          className="overflow-x-hidden p-6 fixed top-0 left-0 right-0 z-99 justify-center items-center w-full inset-0 h-[calc(100%)] backdrop-blur-sm bg-black/30">
          <div className={cn(modalVariants({ variant, className }))}>
            <div
              role="dialog"
              aria-modal="true"
              aria-labelledby={headerText ? headerId : undefined}
              className="bg-emerge-base-3 rounded-lg shadow p-6">
              <div
                className={cn(
                  'flex items-center rounded-t h-auto',
                  !onClose ? 'justify-start' : 'justify-between'
                )}>
                {headerText ? (
                  <h4
                    id={headerId}
                    className={cn(modalHeaderVariant({ headerVariant }), 'text-emerge-navy')}>
                    {headerText}
                  </h4>
                ) : null}
                {onClose ? (
                  <button
                    type="button"
                    className={cn(CloseButtonClass, !headerText ? 'absolute right-4 top-5' : '')}
                    data-modal-hide="static-modal"
                    onClick={() => handleClose()}>
                    <ExitIcon variant="input" size="xs" />
                    <span className="sr-only">Close modal</span>
                  </button>
                ) : null}
              </div>
              <div>{children}</div>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
};

export default Modal;
